import { memo, useContext, useEffect, useRef, useState } from "react";
import Konva from 'konva';
import { Stage, Layer, Rect, Circle, Sprite, Image as KonvaImage, Line } from 'react-konva';
import { QuestionnaireContext } from "../../../StateManagment/Questionnaire";
import { buildRowLookup } from '../../../helpers/analysisHelpers/matrixRowLookup';
import resolveRecordRow from '../../../helpers/analysisHelpers/resolveRecordRow';

const pindropImg = new Image();
pindropImg.src = '/canvas/map/pindrop_icon.png'; 
const imageSize = 50;
 
/**
 * @typedef {Object} MatrixResponse
 * @param {string} ColumnID - The ID of the column.
 * @param {string} FieldID - The ID of the field.
 * @param {string} FieldType - The type of the field, e.g. 'matrix-one'.
 * @param {string} Input - The input value.
 * @param {string} OptionType - The type of option.
 * @param {string} Itemid - The ID of the item.
 * @param {string} RowTitle - The title of the row.
 */

/**
 * MatrixMapGame component renders a grid-based game map with animated elements.
 * @param {Object} props - Component properties.
 * @param {boolean} props.isVisible 
 * @param {import("../FieldType").MatrixQuestionCol[]} props.cols - Columns in the grid.
 * @param {import("../FieldType").MatrixQuestionRow[]} props.rows - Rows in the grid.
 * @param {string} props.scrollContainerSelector - CSS class for the scroll container.
 * @param {MatrixResponse[]} props.responses - Responses to be used in the game.
 */
const MatrixMapGame = ({
    isVisible,
    cols,
    rows,
    scrollContainerSelector,
    // children,
    responses = [], // Responses to be used in the game
}) => {

    const { isTransitioning, setTransitionDuration } = useContext(QuestionnaireContext);

    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    
    const [points, setPoints] = useState([]);
    
    /** @type {React.Ref<Konva.Image>} */
    const objectRef = useRef(null);

    /** @type {React.Ref<Konva.Line>} */
    const lineRef = useRef(null);

    useEffect(() => {
        if (!isVisible) return;
        const resize = () => {
            // if (containerRef.current) {
            //     const { clientWidth, clientHeight } = containerRef.current;
            //     setDimensions({ width: clientWidth, height: clientHeight });
            // }
            const wrapper = document.querySelector(scrollContainerSelector);

            if (!wrapper) return;
            
            // const rect = wrapper.getBoundingClientRect();
            // setDimensions({ width: rect.width, height: rect.height });
            setDimensions({ width: wrapper.scrollWidth, height: wrapper.scrollHeight });
        };

        resize(); // Initial size
        window.addEventListener("resize", resize);

        return () => window.removeEventListener("resize", resize);
    }, [containerRef, isVisible, scrollContainerSelector]);

    const w = dimensions.width;
    const h = dimensions.height;

    const colCount = cols.length + 1;
    const rowCount = rows.length + 1;

    useEffect(() => {
        if (!isVisible) return;
        if (w <= 0) return;

        const colWidth = w / colCount;
        const rowHeight = h / rowCount;

        // Responses find their row by RowID first, the text only as a fallback:
        // a reworded row would otherwise lose its points.
        // AI-CONTEXT: See docs/features/matrix-rowid-join.md
        const lookup = buildRowLookup(rows || []);
        const rowIndexOf = (response) => {
            const def = resolveRecordRow(lookup, response);
            if (!def || !rows) return -1;
            return rows.findIndex(row => (def.ID ? row.ID === def.ID : row.Title === def.Title));
        };

        // Generate points based on responses
        const sortedResponses = responses.sort((a, b) => {
            // Sort by row 'position'.
            const rowA = rows && rows[rowIndexOf(a)];
            const rowB = rows && rows[rowIndexOf(b)];
            return (rowA?.Position || 0) - (rowB?.Position || 0);
        });

        const newPoints = sortedResponses.map((response, index) => {
            const colIndex = cols && cols.findIndex(col => col.ID === response.ColumnID);
            const rowIndex = rowIndexOf(response);
            
            if (colIndex === -1 || rowIndex === -1) return null; // Skip if not found

            const actualColIndex = colIndex + 1;
            const actualRowIndex = rowIndex + 1;

            const x = (colWidth) * actualColIndex + (colWidth) / 2;
            const y = (rowHeight) * actualRowIndex + (rowHeight) / 2;

            return { x, y };
        }).filter(point => point !== null);

        setPoints(newPoints);

    }, [isVisible, w, h, responses, cols, rows, colCount, rowCount]);

    /* Animation */
    const duration = 2000;
    useEffect(() => {
        if (isVisible) {
            setTransitionDuration(duration * 1.4);
        }
    }, [isVisible, setTransitionDuration]);

    useEffect(() => {
        if (!isVisible) return;
        if (!isTransitioning) return;

        // const object = objectRef.current;
        const line = lineRef.current;

        if (!line || w === 0) return;

        // const length = line.getLength();
        function getLineLength(points) {
            let length = 0;
            for (let i = 1; i < points.length; i++) {
                const p1 = points[i - 1];
                const p2 = points[i];
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                length += Math.sqrt(dx * dx + dy * dy);
            }
            return length;
        }

        const totalLength = getLineLength(points);

        const anim = new Konva.Animation((frame) => {
            if (!frame) return;

            // @todo animated object over the path. 

            const progress = Math.min(1, frame.time / duration); /* progress 0-1 */
            const visibleLength = totalLength * progress;
            const invisibleLength = totalLength - visibleLength;

            line.dash([totalLength, invisibleLength]);
            line.dashOffset(totalLength - visibleLength - (totalLength * 0.025)); // Slight offset to prevent showing the end of the line at the final point
        }, line.getLayer());

        anim.start();
        // object.start(); // Start animation (for <Sprite> only)

        return () => { anim.stop() };
    }, [isVisible, objectRef, w, h, isTransitioning, setTransitionDuration, points]);

    // If not visible, return null
    if (!isVisible) 
        return null; 

    // convert points to a flat array for the Line component
    const linePoints = points.flatMap(point => [point.x, point.y]);

    return (
        <div ref={containerRef} style={{ pointerEvents: 'none', 
            position: 'absolute', zIndex: -1, 
            top: 0, left: 0, 
            // width: '100%',
            width: dimensions.width,
            height: '100%',
            backgroundImage: 'url("/canvas/map/map.png")', 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
            backgroundRepeat: 'no-repeat',
            boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.4)'
            }}>
            <Stage width={dimensions.width} height={dimensions.height}>
                <Layer>
                    {/* Debug: render cols and rows, e.g. for each row, draw cols as rects */}
                    {/* {rows.map((row, rowIndex) => (
                        <Rect
                            key={row.ID}
                            x={0}
                            y={(h / rowCount) * (rowIndex + 1)}
                            width={w}
                            height={h / rowCount}
                            fill="rgba(255, 255, 255, 0.5)"
                            stroke="black"
                            strokeWidth={1}
                        />
                    ))}
                    {cols.map((col, colIndex) => (
                        <Rect
                            key={col.ID}
                            x={(w / colCount) * (colIndex + 1)}
                            y={0}
                            width={w / colCount}
                            height={h}
                            fill="rgba(255, 255, 255, 0.5)"
                            stroke="black"
                            strokeWidth={1}
                        />
                    ))} */}
                    <Line
                        ref={lineRef}
                        name="progress-line"
                        visible={isTransitioning}
                        points={linePoints}
                        // stroke="purple"
                        stroke="blue"
                        opacity={0.4}
                        strokeWidth={5}
                        lineCap="round"
                        lineJoin="round"
                        // dash={[10, 10]}
                        tension={0.4}
                        // globalCompositeOperation="source-over"
                    />
                    {/* At each point, render an image, using KonvaImage and the image on the top of the file */}
                    {points.map((point, index) => (
                        <KonvaImage
                            visible={isTransitioning}
                            key={index}
                            image={pindropImg}
                            x={point.x}
                            y={point.y}
                            width={imageSize}
                            height={imageSize}
                            offset={{ 
                                x: imageSize / 2, 
                                y: imageSize // bottom aligned 
                            }}
                        />
                    ))}
                </Layer>
            </Stage>

            {/* When the game is active, adjust the styles of the matrix questions */}
            <style>
                {`
                    .matrix-grid-header p {
                        color: white !important;
                    }
                    .matrix-grid-row p {
                        color: white !important;
                    }

                    .matrix-grid-header,
                    .matrix-grid-item {
                        border-bottom: 1px solid #375a4a !important;
                    }
                `}
            </style>
        </div>
    );
}

// export default MatrixMapGame;

export default memo(MatrixMapGame, (prevProps, nextProps) => {
    return prevProps.cols === nextProps.cols &&
        prevProps.rows === nextProps.rows &&
        prevProps.responses === nextProps.responses;
});
